/**
 * `df-42a9cd00b2ab`: talking to the Elasticsearch food-search backend over HTTP.
 *
 * Why this is its own module. This is an HTTP client with a set of concerns entirely its
 * own — waiting for Firebase to finish signing the user in, getting a token, retrying once
 * when the server says the token was no good, picking the backend origin this build is
 * routed to, and telling a passing network blip apart from a real failure. None of that has
 * anything to do with reading food documents out of the local database, which is what the
 * service this came out of is for.
 *
 * It was lifted out of `food-db.service.ts` because that class pulls in the whole Angular
 * dependency graph and so will not load under the node test runner, which meant none of the
 * branches below could be covered by a test that actually runs them. Everything this module
 * borrows arrives as a plain parameter rather than an import, for exactly that reason: the
 * only things imported here are types, the pure failure classifier next door, the shared
 * timeout helper and the shared name-search query, so the module loads under node and its test
 * drives the real code.
 *
 * Behaviour is unchanged from what lived in the service.
 */

import type { FoodHeader, FoodStatus, Nutrients } from '@cf-shared/data-model/food';
import type { UiDisplayNutrient } from 'models/display-nutrient';
import { buildFoodHeader } from './food-header-builder';
import { timeout, TimeoutError } from '@cf-shared/shared-util';
import { buildFoodNameSearchQuery, TypoTolerance } from '@cf-shared/food-search/food-search-query';
import { readFoodSearchHit } from '@cf-shared/food-search/food-search-hit';
import { SearchConnectionError, ReportedSearchFailureError, SearchRequestPhase, SearchTimeoutError, classifySearchFailure, SearchFailureOutcome, needsStallConnectionCheck } from './food-search-error';

/**
 * How long we wait for Firebase to finish signing the user in before searching anyway.
 *
 * The warm-resume fast path added in v1.17.1 (#3184) lets the interface run before Firebase
 * has settled, so reading the signed-in user without waiting would miss the token and the
 * request would come back unauthorised, which used to show as an empty result (#3246). The
 * wait is capped so that a Firebase that never settles cannot block search for ever — the
 * signed-out check below is what handles that case.
 */
export const AUTH_SETTLED_WAIT_MS = 10_000;

/**
 * How long one search may wait for the server once it starts talking to it (#5320; the
 * background is in design-docs/elasticsearch-infrastructure.md).
 *
 * Why fifteen seconds: a healthy search answers in well under a second, and the server's own
 * retry against Elasticsearch sleeps 2.5 s in total, so a deadline much under ten seconds would
 * throw away answers that were about to arrive. It is also what the barcode lookup of the
 * user's own foods already allows (`eanSearchWrapper` in food-db.service.ts).
 */
export const SEARCH_REQUEST_TIMEOUT_MS = 15_000;

/**
 * How long the connection check may take once a search has run out of time with no answer
 * (df-bac7bf9dc8fa). The user is already looking at a stalled search, so this is kept short: on
 * a working network the check's tiny static file answers in well under a second.
 */
export const SEARCH_STALL_CONNECTION_CHECK_MS = 4_000;

/**
 * Searches shorter than this are not sent to the server at all. The shortest search worth
 * running is a three-letter word such as "egg".
 */
export const MIN_SEARCH_TEXT_LENGTH = 3;

/** The little of an HTTP response this module reads. A real `Response` satisfies it. */
export interface ElasticHttpResponse {
  status: number;
  statusText: string;
  ok: boolean;
  json(): Promise<any>;
}

/** The little of a signed-in Firebase user this module reads. */
export interface ElasticAuthUser {
  getIdToken(forceRefresh: boolean): Promise<string>;
}

/**
 * Everything this module borrows from its caller. All of it is passed in rather than
 * imported, so the module stays loadable under the node test runner.
 */
export interface ElasticSearchDeps {
  /** resolves once Firebase has finished deciding who, if anyone, is signed in */
  authSettled: Promise<void>;
  /** how long to wait for that before searching anyway; the app passes AUTH_SETTLED_WAIT_MS */
  authSettledWaitMs: number;
  /** the signed-in Firebase user, or null/undefined when nobody is signed in */
  getCurrentUser: () => ElasticAuthUser | null | undefined;
  /** how long the request may take once started; the app passes SEARCH_REQUEST_TIMEOUT_MS */
  searchTimeoutMs: number;
  /** the origin of the backend this build is routed to */
  resolveApiOriginAsync: () => Promise<string>;
  /** issues the request; in the app this is the global fetch. `signal` is aborted when the deadline runs out */
  fetchImpl: (url: string, init: { method: string; headers: Record<string, string>; body: string; signal?: AbortSignal }) => Promise<ElasticHttpResponse>;
  /** turns a food's stored nutrients into the numbers the interface shows */
  formatNutrients: (nutrients: Nutrients) => UiDisplayNutrient[];
  /** works out whether a food is official, private, contributed and so on, from its id */
  getFoodStatus: (foodId: string) => FoodStatus;
  /** reports a genuine (non-transient) search failure to the error tracker */
  reportRealFailure: (err: any) => void;
  /** whether the app's connection status says it is online; the app passes OnlineStateService.isLikelyOnline (#6391) */
  isLikelyOnline: () => boolean;
  /**
   * whether our server answers a tiny static request right now; never throws. The app passes
   * OnlineStateService.probeServerReachableAsync with SEARCH_STALL_CONNECTION_CHECK_MS
   * (df-bac7bf9dc8fa)
   */
  checkConnectionAsync: () => Promise<boolean>;
}

/**
 * Runs one already-built query against the search backend and turns its hits into food
 * headers.
 *
 * Once it starts talking to the server it gives up after `deps.searchTimeoutMs` (#5320). That
 * failure is a `SearchTimeoutError`, reported and thrown like any other real failure below.
 *
 * Throws `SearchConnectionError` when the request failed for a reason that looks temporary,
 * so the caller can say "could not load — connection problem" instead of "no results"
 * (#3743). A temporary failure is deliberately NOT reported to the error tracker, so passing
 * network blips do not bury real problems.
 *
 * Any other failure is reported here and then thrown as a `ReportedSearchFailureError`
 * carrying the original cause, so a caller can still tell a failure apart from genuinely
 * finding nothing AND can tell that the failure has already been reported. A caller that
 * reports it again files two Sentry events for one cause.
 *
 * Not every failure leaving this function is one of those two. The sign-in wait below
 * rethrows a non-timeout rejection before the reporting catch is reached, so that error
 * arrives at the caller unwrapped and unreported, and the caller must report it.
 */
export async function runElasticQueryAsync(queryJSON: string, deps: ElasticSearchDeps): Promise<FoodHeader[]> {

  let startTime = Date.now();
  let serverTookMs = undefined;
  let fetchTookMs = undefined;
  let headers: FoodHeader[] = [];

  // wait for Firebase to populate the signed-in user before reading it. narrow the catch to
  // TimeoutError so a future change that rejects the settled promise isn't silently treated
  // as a timeout. timeout() cancels its timer on resolve, so this leaks nothing.
  try {
    await timeout(deps.authSettledWaitMs, deps.authSettled);
  } catch (e) {
    if (e?.name === 'TimeoutError') {
      console.log('Elastic query: proceeding without Firebase auth-settled (timed out)');
    } else {
      throw e;
    }
  }

  // if no Firebase user is signed in by now, this is a genuinely logged-out
  // session — return [] silently rather than firing an unauthenticated
  // request that the server will 401 on every keystroke. the caller's
  // empty-state UI is the right behavior for a logged-out user.
  if (!deps.getCurrentUser()) {
    console.log('Elastic query: no Firebase user, returning empty result');
    return [];
  }

  // how far the request has got, so a deadline that runs out can say where it stopped (#5320)
  let phase = SearchRequestPhase.Token;
  // one controller for the whole request, so both attempts below are cancelled together
  const abortController = new AbortController();

  // issue the actual ES request; on a 401 we force-refresh the token and
  // retry once, since the most plausible cause is our local token being
  // stale (e.g. the listener fired but the refresh lagged). if the token
  // refresh itself fails (transient Firebase network error), fall through
  // to the catch below where classifySearchFailure recognizes it as transient
  // and throws SearchConnectionError without polluting Sentry.
  const doFetch = async (apiOrigin: string, forceRefresh: boolean): Promise<ElasticHttpResponse> => {
    phase = SearchRequestPhase.Token;
    const user = deps.getCurrentUser();
    const token = user ? await user.getIdToken(forceRefresh) : null;
    phase = SearchRequestPhase.Request;
    return deps.fetchImpl(`${apiOrigin}/api/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: queryJSON,
      signal: abortController.signal,
    });
  };

  // everything the user waits on once we start talking to the server: the request, the one
  // retry after a 401, and reading the answer. All of it shares the one deadline below.
  const requestAndReadAsync = async (apiOrigin: string): Promise<any> => {
    let response = await doFetch(apiOrigin, false);
    if (response.status === 401) {
      // stale token edge case — refresh once and retry before giving up
      console.log('Elastic query: 401, retrying after forced token refresh');
      response = await doFetch(apiOrigin, true);
    }

    // treat any non-2xx as an error rather than zero-hits. previous code
    // conflated server failures with empty result sets via the swallow-all
    // catch below (#3246).
    if (!response.ok) {
      throw new Error(`Elastic search failed: HTTP ${response.status} ${response.statusText}`);
    }

    phase = SearchRequestPhase.Body;
    return response.json();
  };

  try {
    // route-sensitive (#4405): /api/search runs authenticated server logic, so
    // it must reach the server this build is actually routed to. The await is
    // free once the backend route is known, which is every boot but the first.
    //
    // It is awaited BEFORE the deadline below starts, not inside it. On a first launch this
    // wait has its own 15-second limit, and when that runs out it throws
    // BackendRouteUnresolvedError, which means the network has not answered — a connection
    // problem, classified as such. Inside the deadline, the deadline's equal timer would always
    // fire first and report an offline first launch as a slow server.
    const apiOrigin = await deps.resolveApiOriginAsync();

    // #5320: give up after searchTimeoutMs instead of waiting for the operating system. The
    // deadline cannot rely on the abort signal to end the wait — see design-docs/elasticsearch-infrastructure.md.
    const requestStartMs = Date.now();
    let result: any;
    try {
      result = await timeout(deps.searchTimeoutMs, requestAndReadAsync(apiOrigin));
    } catch (e) {
      // only timeout()'s own error means the deadline ran out; anything else is the request's
      // own failure and goes on to be classified below as it always did
      if (!(e instanceof TimeoutError)) {
        throw e;
      }
      // read the connection status now, at the deadline: a token stall on a phone the app already
      // knows is offline is a connection problem, not a slow server (#6391)
      const elapsedMs = Date.now() - requestStartMs;
      const stoppedAt = phase;
      const deviceLikelyOnline = deps.isLikelyOnline();
      console.log(`Elastic query: no answer within ${deps.searchTimeoutMs} ms (stopped at phase "${stoppedAt}"), giving up`);
      // if no answer had started arriving, ask whether our server is reachable at all: a phone
      // whose network is quietly failing while the app still says online would otherwise be
      // filed as a slow server (df-bac7bf9dc8fa). A body stall proves the network worked, and an
      // offline token stall is already settled by #6391, so neither runs the check.
      let connectionCheckPassed: boolean | undefined = undefined;
      if (needsStallConnectionCheck(stoppedAt, deviceLikelyOnline)) {
        connectionCheckPassed = await deps.checkConnectionAsync();
        console.log(`Elastic query: connection check after the stall ${connectionCheckPassed ? 'passed' : 'failed'}`);
      }
      const timedOut = new SearchTimeoutError(deps.searchTimeoutMs, elapsedMs, stoppedAt, deviceLikelyOnline, connectionCheckPassed);
      // cancel only now, after the deadline has decided the outcome, and with the deadline as
      // the reason. A browser fetch cancelled any other way fails with an AbortError, which
      // classifySearchFailure counts as a passing connection blip — so the web build would call
      // a stall a blip while a phone called it a failure. timeout() absorbs the request's late
      // failure, so nothing is left unhandled.
      abortController.abort(timedOut);
      throw timedOut;
    }
    if (result.error) {
      throw new Error(JSON.stringify(result.error));
    }
    let hits = result?.hits?.hits;
    serverTookMs = result.took;

    if (!hits) {
      return [];
    }
    fetchTookMs = Date.now() - startTime;

    for (let i = 0; i < hits.length; i++) {
      let h = hits[i];
      // the same reading of a hit the server does for a meal scan (#5623)
      const hit = readFoodSearchHit(h);
      let header: FoodHeader = {
        ...buildFoodHeader(
          { _id: hit._id, name: hit.name, nutrients: hit.nutrients },
          { formatNutrients: deps.formatNutrients, status: deps.getFoodStatus(hit._id) },
        ),
        score: hit.score,
        elasticHit: h,
        altNames: hit.altNames,
      };
      headers.push(header);
    }

  } catch (e) {
    // distinguish a transient connectivity failure (offline blip, iOS "Load
    // failed" / "network connection was lost", or a Firebase token refresh that
    // hit a network error) from a real error. On a transient failure throw a
    // typed SearchConnectionError so the caller can show an honest "couldn't
    // load — connection problem" state instead of "no results" (#3743). We do
    // NOT report it, so Sentry stays clean for blips — same property the old
    // empty-array return had, but now the caller can tell the difference
    // between "request failed" and "genuinely found nothing".
    if (classifySearchFailure(e) === SearchFailureOutcome.Transient) {
      console.log('Elastic query: transient connection error, signalling caller', e?.message || e);
      throw new SearchConnectionError(e);
    }
    // real error — log it AND propagate so callers can distinguish failure
    // from "no hits". We report it here and nowhere else: the thrown
    // ReportedSearchFailureError is what tells every caller that this failure has
    // already reached the error tracker, so their own catch must not file it a
    // second time under a different name. The console.error below lands on that
    // one Sentry event as a breadcrumb carrying the query.
    console.error('Error in Elastic query', queryJSON);
    deps.reportRealFailure(e);
    throw new ReportedSearchFailureError(e);
  } finally {
    let elapsedTime = Date.now() - startTime;
    console.log(`Elastic search took ${elapsedTime} ms on the client total. ${fetchTookMs} ms for the client fetch, ${serverTookMs} ms on the server`);
  }

  //console.log('Elastic search results:', headers);
  return headers;
}

/** Builds the query that finds foods carrying any of the given barcodes. */
export function buildFoodEanSearchQuery(EANs: string[], maxCount: number): string {
  return JSON.stringify({
    size: maxCount,
    "query": {
      "terms": {
        "EANs": EANs
      }
    }
  });
}

/** Searches the backend by name. A search too short to be worth sending returns nothing. */
export async function searchFoodHeadersByNameAsync(
  searchText: string, maxCount: number, deps: ElasticSearchDeps, typoTolerance: TypoTolerance = TypoTolerance.OneLetter,
): Promise<FoodHeader[]> {
  // if search is too short to make sense (shortest word usefull is "egg")
  if (searchText.length < MIN_SEARCH_TEXT_LENGTH) {
    // don't waste time calling up the server
    return [];
  }
  return runElasticQueryAsync(buildFoodNameSearchQuery(searchText, maxCount, typoTolerance), deps);
}

/** Searches the backend by barcode. */
export async function searchFoodHeadersByEANsAsync(EANs: string[], maxCount: number, deps: ElasticSearchDeps): Promise<FoodHeader[]> {
  return runElasticQueryAsync(buildFoodEanSearchQuery(EANs, maxCount), deps);
}
